import { useCallback, useSyncExternalStore } from 'react'
import type { DataOrigin } from '../types'
import { scopedDashboardKey } from './usePersistentState'

const STORAGE_KEY = 'bd.data_origin'
const DEFAULT: DataOrigin = 'all'

type OriginStore = {
  value: DataOrigin
  listeners: Set<() => void>
}

const stores = new Map<string, OriginStore>()

function readStoredOrigin(storageKey: string): DataOrigin {
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    if (raw !== null) return JSON.parse(raw) as DataOrigin
  } catch {
    /* storage unavailable or corrupt */
  }
  return DEFAULT
}

function getStore(storageKey: string): OriginStore {
  let store = stores.get(storageKey)
  if (!store) {
    store = { value: readStoredOrigin(storageKey), listeners: new Set() }
    stores.set(storageKey, store)
  }
  return store
}

function setStoredOrigin(storageKey: string, value: DataOrigin) {
  const store = getStore(storageKey)
  if (value === store.value) return
  store.value = value
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(value))
  } catch {
    /* quota exceeded or storage unavailable */
  }
  store.listeners.forEach((listener) => listener())
}

/** Drop in-memory origin stores after login/logout clears sessionStorage. */
export function resetDataOriginStore(): void {
  stores.clear()
}

/** Shared Old / New / All source filter across all Business Analytics views. */
export function useDataOrigin(): [DataOrigin, (value: DataOrigin) => void] {
  const storageKey = scopedDashboardKey(STORAGE_KEY)

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const store = getStore(storageKey)
      store.listeners.add(onStoreChange)
      return () => {
        store.listeners.delete(onStoreChange)
      }
    },
    [storageKey],
  )

  const getSnapshot = useCallback(() => getStore(storageKey).value, [storageKey])

  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const setValue = useCallback(
    (next: DataOrigin) => {
      setStoredOrigin(storageKey, next)
    },
    [storageKey],
  )

  return [value, setValue]
}
